/**
 * Matching Logic Utility
 *
 * Handles both one-to-one and category-based matching modes.
 * This module provides functions to detect matching mode, validate matches,
 * and calculate scores for the Universal Matching Game.
 */

export type MatchingMode = 'one-to-one' | 'category' | 'sort' | 'multiple-choice';

export interface DictionaryMetadata {
  matchingMode: MatchingMode;
  categories?: Record<string, string[]>; // For category mode
}

export interface SortDictionaryMetadata {
  matchingMode: 'sort';
}

export interface CategoryDictionaryData {
  items: Record<string, string>;
  metadata: DictionaryMetadata;
}

export interface SortDictionaryData {
  items: Record<string, string>; // item → label mapping
  labels: string[]; // Array of label names
  metadata: SortDictionaryMetadata;
}

export type DictionaryData = Record<string, string> | CategoryDictionaryData | SortDictionaryData;

export type DropZoneId = 'upper' | 'lower' | 'item1' | 'item2' | 'category' | string;

export interface DropZone {
  id: DropZoneId;
  content: string | null;
  zoneType?: 'default' | 'item' | 'category' | 'label';
  labelName?: string; // For sort mode label boxes
}

export interface MatchResult {
  isMatch: boolean;
  mode: MatchingMode;
  pointsAwarded: number;
  itemsMatched: number;
  message?: string;
  matchedItem?: string; // For sort mode - which item was matched
}

/**
 * Detect if a dictionary uses sort-based matching
 */
export function isSortDictionary(data: any): boolean {
  return data?.metadata?.matchingMode === 'sort' ||
         (data?.labels && Array.isArray(data.labels));
}

/**
 * Detect if a dictionary uses category-based matching
 */
export function isCategoryDictionary(data: any): boolean {
  return data?.metadata?.matchingMode === 'category' ||
         (data?.items && data?.metadata?.categories);
}

/**
 * Detect the matching mode of a dictionary
 */
export function detectMatchingMode(data: any): MatchingMode {
  if (isMultipleChoiceDictionary(data)) {
    return 'multiple-choice';
  }
  if (isSortDictionary(data)) {
    return 'sort';
  }
  if (isCategoryDictionary(data)) {
    return 'category';
  }
  return 'one-to-one';
}

/**
 * Check if dictionary is for multiple choice mode
 */
export function isMultipleChoiceDictionary(data: any): boolean {
  return data?.metadata?.matchingMode === 'multiple-choice';
}

/**
 * Get the flat items from a dictionary (works for both modes)
 */
export function getDictionaryItems(data: any): Record<string, string> {
  if (isCategoryDictionary(data)) {
    return data.items || {};
  }
  return data || {};
}

/**
 * Get categories from a category dictionary
 */
export function getCategories(data: any): Record<string, string[]> {
  if (isCategoryDictionary(data)) {
    return data.metadata?.categories || {};
  }
  return {};
}

/**
 * Get all category names from a dictionary
 */
export function getCategoryNames(data: any): string[] {
  const categories = getCategories(data);
  return Object.keys(categories);
}

/**
 * Get all items (non-categories) from a category dictionary
 */
export function getCategoryItems(data: any): string[] {
  const categories = getCategories(data);
  const allItems: string[] = [];
  Object.values(categories).forEach(items => {
    if (Array.isArray(items)) {
      allItems.push(...items);
    }
  });
  return allItems;
}

/**
 * Check if a string is a category (not an item)
 */
export function isCategory(item: string, data: any): boolean {
  const categories = getCategories(data);
  return Object.keys(categories).includes(item);
}

/**
 * Check if an item belongs to a specific category
 */
export function itemBelongsToCategory(item: string, category: string, data: any): boolean {
  const categories = getCategories(data);
  const categoryItems = categories[category];
  return Array.isArray(categoryItems) && categoryItems.includes(item);
}

/**
 * Validate a sort dictionary structure
 */
export function validateSortDictionary(data: any): { valid: boolean; error?: string } {
  if (!data.labels || !Array.isArray(data.labels)) {
    return { valid: false, error: 'Missing labels array' };
  }

  if (data.labels.length < 2 || data.labels.length > 8) {
    return { valid: false, error: 'Labels must be 2-8 count' };
  }

  if (!data.items || typeof data.items !== 'object') {
    return { valid: false, error: 'Missing items object' };
  }

  // Verify each label has at least one item
  const itemCounts: Record<string, number> = {};
  data.labels.forEach((label: string) => { itemCounts[label] = 0; });

  Object.values(data.items).forEach((label: any) => {
    if (typeof label === 'string' && itemCounts.hasOwnProperty(label)) {
      itemCounts[label]++;
    }
  });

  for (const [label, count] of Object.entries(itemCounts)) {
    if (count === 0) {
      return { valid: false, error: `Label "${label}" has no items` };
    }
  }

  return { valid: true };
}

/**
 * Perform one-to-one match validation
 */
function checkOneToOneMatch(
  dropZones: DropZone[],
  data: any
): MatchResult {
  const item1 = dropZones.find(z => z.id === 'upper')?.content;
  const item2 = dropZones.find(z => z.id === 'lower')?.content;

  if (!item1 || !item2) {
    return {
      isMatch: false,
      mode: 'one-to-one',
      pointsAwarded: 0,
      itemsMatched: 0,
      message: 'Both zones must be filled'
    };
  }

  const dict = getDictionaryItems(data);
  const isMatch = dict[item1] === item2 || dict[item2] === item1;

  return {
    isMatch,
    mode: 'one-to-one',
    pointsAwarded: isMatch ? 2 : -1,
    itemsMatched: isMatch ? 2 : 0,
    message: isMatch ? 'Correct match!' : 'Wrong match - try again!'
  };
}

/**
 * Perform sort mode match validation
 */
function checkSortMatch(
  dropZones: DropZone[],
  data: any
): MatchResult {
  // Find the filled zone (should only be one in sort mode)
  const filledZone = dropZones.find(z => z.content !== null);

  if (!filledZone) {
    return {
      isMatch: false,
      mode: 'sort',
      pointsAwarded: 0,
      itemsMatched: 0,
      message: 'No item dropped'
    };
  }

  const item = filledZone.content;
  const targetLabel = filledZone.labelName;

  if (!item) {
    return {
      isMatch: false,
      mode: 'sort',
      pointsAwarded: 0,
      itemsMatched: 0,
      message: 'No item'
    };
  }

  const correctLabel = data.items[item];
  const isMatch = correctLabel === targetLabel;

  return {
    isMatch,
    mode: 'sort',
    pointsAwarded: isMatch ? 1 : -1, // +1 for correct, -1 for wrong
    itemsMatched: isMatch ? 1 : 0,
    matchedItem: item,
    message: isMatch
      ? `✓ ${item} belongs to ${targetLabel}!`
      : `✗ ${item} doesn't belong to ${targetLabel}`
  };
}

/**
 * Perform category-based match validation
 */
function checkCategoryMatch(
  dropZones: DropZone[],
  data: any
): MatchResult {
  const item1 = dropZones.find(z => z.id === 'item1')?.content;
  const item2 = dropZones.find(z => z.id === 'item2')?.content;
  const category = dropZones.find(z => z.id === 'category')?.content;

  if (!item1 || !item2 || !category) {
    return {
      isMatch: false,
      mode: 'category',
      pointsAwarded: 0,
      itemsMatched: 0,
      message: 'All three zones must be filled'
    };
  }

  const categories = getCategories(data);
  const categoryItems = categories[category];

  if (!categoryItems || !Array.isArray(categoryItems)) {
    return {
      isMatch: false,
      mode: 'category',
      pointsAwarded: -1,
      itemsMatched: 0,
      message: `"${category}" is not a valid category`
    };
  }

  const item1Match = categoryItems.includes(item1);
  const item2Match = categoryItems.includes(item2);

  // Both items must belong to the category
  const isMatch = item1Match && item2Match;

  if (isMatch) {
    return {
      isMatch: true,
      mode: 'category',
      pointsAwarded: 4, // 2 points per item
      itemsMatched: 2,
      message: `Both items belong to ${category}!`
    };
  }

  // Provide helpful feedback
  let message = 'Wrong match!';
  if (!item1Match && !item2Match) {
    message = `Neither item belongs to ${category}`;
  } else if (!item1Match) {
    message = `"${item1}" doesn't belong to ${category}`;
  } else if (!item2Match) {
    message = `"${item2}" doesn't belong to ${category}`;
  }

  return {
    isMatch: false,
    mode: 'category',
    pointsAwarded: -1,
    itemsMatched: 0,
    message
  };
}

/**
 * Main match checking function - works for all three modes
 */
export function checkMatch(
  dropZones: DropZone[],
  dictionaryData: any
): MatchResult {
  const mode = detectMatchingMode(dictionaryData);

  if (mode === 'sort') {
    return checkSortMatch(dropZones, dictionaryData);
  } else if (mode === 'category') {
    return checkCategoryMatch(dropZones, dictionaryData);
  } else {
    return checkOneToOneMatch(dropZones, dictionaryData);
  }
}

/**
 * Get game items for display (all rectangles that should appear)
 */
export function getGameItemsForMode(data: any, pairCount: number): string[] {
  const mode = detectMatchingMode(data);

  if (mode === 'sort') {
    // Return all items (excluding labels)
    const items = Object.keys(data.items || {});
    // Shuffle and return
    return items.sort(() => Math.random() - 0.5);
  } else if (mode === 'category') {
    return getCategoryGameItems(data, pairCount);
  } else {
    return getOneToOneGameItems(data, pairCount);
  }
}

/**
 * Get game items for one-to-one mode
 */
function getOneToOneGameItems(data: any, pairCount: number): string[] {
  const dict = getDictionaryItems(data);
  const processedPairs = new Set<string>();
  const allPairs: Array<[string, string]> = [];

  Object.entries(dict).forEach(([key, value]) => {
    const pairId = [key, String(value)].sort().join('|');
    if (!processedPairs.has(pairId)) {
      processedPairs.add(pairId);
      allPairs.push([key, String(value)]);
    }
  });

  // Shuffle and select pairs
  const shuffledPairs = allPairs.sort(() => Math.random() - 0.5);
  const selectedPairs = shuffledPairs.slice(0, pairCount);

  // Return both items from each pair
  const gameItems: string[] = [];
  selectedPairs.forEach(([item1, item2]) => {
    gameItems.push(item1, item2);
  });

  return gameItems;
}

/**
 * Get game items for category mode
 */
function getCategoryGameItems(data: any, pairCount: number): string[] {
  const categories = getCategories(data);
  const categoryNames = Object.keys(categories);
  const gameItems: string[] = [];
  const usedItems = new Set<string>();

  // Each "match" in category mode = 3 rectangles: item1 + item2 + category name
  // Each item-pair needs its OWN category rectangle (even if same category)
  let matchesNeeded = pairCount;

  // Shuffle category names
  const shuffledCategories = [...categoryNames].sort(() => Math.random() - 0.5);

  for (const categoryName of shuffledCategories) {
    if (matchesNeeded <= 0) break;

    const categoryItems = categories[categoryName];
    if (!categoryItems || categoryItems.length < 2) continue;

    // Shuffle items within category
    const shuffledItems = [...categoryItems].sort(() => Math.random() - 0.5);

    // Take pairs of items from this category
    for (let i = 0; i < shuffledItems.length - 1 && matchesNeeded > 0; i += 2) {
      const item1 = shuffledItems[i];
      const item2 = shuffledItems[i + 1];

      if (!usedItems.has(item1) && !usedItems.has(item2)) {
        // Add all 3 as a triple: item1, item2, category
        gameItems.push(item1, item2, categoryName);
        usedItems.add(item1);
        usedItems.add(item2);
        matchesNeeded--;
      }
    }
  }

  // Shuffle all items together
  return gameItems.sort(() => Math.random() - 0.5);
}

/**
 * Calculate max pairs available in a dictionary
 */
export function getMaxPairsForMode(data: any): number {
  const mode = detectMatchingMode(data);

  if (mode === 'sort') {
    // In sort mode, each item is a "pair" (item → label)
    return Object.keys(data.items || {}).length;
  } else if (mode === 'category') {
    const categories = getCategories(data);
    let totalItems = 0;
    Object.values(categories).forEach(items => {
      if (Array.isArray(items)) {
        totalItems += items.length;
      }
    });
    // In category mode, we match 2 items at a time
    return Math.floor(totalItems / 2);
  } else {
    const dict = getDictionaryItems(data);
    const processedPairs = new Set<string>();

    Object.entries(dict).forEach(([key, value]) => {
      const pairId = [key, String(value)].sort().join('|');
      processedPairs.add(pairId);
    });

    return processedPairs.size;
  }
}

/**
 * Get initial drop zones based on matching mode
 */
export function getInitialDropZones(mode: MatchingMode): DropZone[] {
  if (mode === 'category') {
    return [
      { id: 'item1', content: null, zoneType: 'item' },
      { id: 'item2', content: null, zoneType: 'item' },
      { id: 'category', content: null, zoneType: 'category' }
    ];
  } else {
    return [
      { id: 'upper', content: null, zoneType: 'default' },
      { id: 'lower', content: null, zoneType: 'default' }
    ];
  }
}

/**
 * Check if all required zones are filled for the current mode
 */
export function areAllZonesFilled(dropZones: DropZone[], mode: MatchingMode): boolean {
  if (mode === 'category') {
    const item1 = dropZones.find(z => z.id === 'item1')?.content;
    const item2 = dropZones.find(z => z.id === 'item2')?.content;
    const category = dropZones.find(z => z.id === 'category')?.content;
    return !!(item1 && item2 && category);
  } else {
    const upper = dropZones.find(z => z.id === 'upper')?.content;
    const lower = dropZones.find(z => z.id === 'lower')?.content;
    return !!(upper && lower);
  }
}

/**
 * Get all filled items from drop zones
 */
export function getFilledItems(dropZones: DropZone[]): string[] {
  return dropZones
    .filter(z => z.content !== null)
    .map(z => z.content as string);
}

/**
 * Clear all drop zones
 */
export function clearDropZones(dropZones: DropZone[]): DropZone[] {
  return dropZones.map(z => ({ ...z, content: null }));
}
